import { useEffect, useState } from 'react';
import Mascot from '../components/Mascot';
import StreakBadge from '../components/StreakBadge';
import { chapters } from '../content/chapters';
import { chapterScores, problemsSolved, totalMinutes } from '../lib/stats';
import { computeStreak } from '../lib/streak';

export default function ProgressPage() {
  const [streak, setStreak] = useState(0);
  const [solved, setSolved] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [scores, setScores] = useState<Record<string, number>>({});

  useEffect(() => {
    Promise.all([computeStreak(), problemsSolved(), totalMinutes(), chapterScores()]).then(
      ([s, p, m, cs]) => {
        setStreak(s);
        setSolved(p);
        setMinutes(m);
        setScores(cs);
      }
    );
  }, []);

  const chapterAvg = (chapterNum: number) => {
    const ids = chapters[chapterNum - 1].sections.map((s) => s.id);
    const present = ids.map((id) => scores[id]).filter((v) => typeof v === 'number') as number[];
    if (present.length === 0) return null;
    return Math.round(present.reduce((a, b) => a + b, 0) / present.length);
  };

  return (
    <div className="max-w-xl mx-auto px-5 pt-10 safe-top space-y-6">
      <header className="flex items-center gap-3">
        <Mascot mood="celebrating" size={56} />
        <div>
          <h1 className="font-display font-black text-2xl leading-none">Your progress</h1>
          <p className="text-ink/70 text-sm">Keep it up, Grace.</p>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <div className="card p-4 text-center">
          <div className="text-xs text-ink/60">Problems</div>
          <div className="font-display text-3xl">{solved}</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-xs text-ink/60">Minutes</div>
          <div className="font-display text-3xl">{minutes}</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-xs text-ink/60">Streak</div>
          <div className="font-display text-3xl">{streak}</div>
        </div>
      </div>

      <div className="flex justify-center"><StreakBadge streak={streak} /></div>

      <div className="card p-5 space-y-3">
        <div className="font-display text-lg">Chapter understanding</div>
        {chapters.map((c) => {
          const avg = chapterAvg(c.num);
          return (
            <div key={c.num} className="flex items-center gap-3">
              <div className="w-6 text-ink/50 text-sm">{c.num}</div>
              <div className="flex-1 truncate text-sm">{c.title}</div>
              <div className="w-24 h-2 rounded-full bg-ink/5 overflow-hidden">
                {avg !== null && (
                  <div
                    className="h-full bg-sky"
                    style={{ width: `${avg}%` }}
                  />
                )}
              </div>
              <div className="w-10 text-right text-sm font-semibold text-ink/70">
                {avg === null ? '—' : `${avg}%`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
